import {isAfter} from 'date-fns';
import {Box, Spinner, Text} from 'platform/foundation';
import styled from 'styled-components';

import {SyntheticEvent, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {useGetTelephonyCallRecordingQuery} from '@dms/api/settings';
import i18n from '@dms/i18n';

import {parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {handleApiError} from '../../utils/handleApiError';

/** The backend hands out no link valid for less than a minute. */
const LINK_MIN_VALIDITY_MS = 60_000;

interface CallRecordingPlayerProps extends TestIdProps {
  callId: string;
}

/**
 * The browser's own audio player for a call recording: seek and speed come with it; its Download
 * item is hidden.
 * Mounted when the viewer presses Play, which is when the signed link is asked for. The link
 * expires; when the viewer plays again after that, or loading fails past it, the player asks for a
 * new one and goes on from where it was.
 */
export function CallRecordingPlayer(props: CallRecordingPlayerProps) {
  const {
    data: recording,
    isLoading,
    isError,
    refetch,
    fulfilledTimeStamp,
  } = useGetTelephonyCallRecordingQuery({
    callId: props.callId,
  });
  const [resumeAt, setResumeAt] = useState<number | null>(null);

  if (isLoading) {
    return <Spinner size="small" />;
  }

  if (isError || isNil(recording)) {
    return (
      <Text size="small" color="secondary" data-testid={suffixTestId('unavailable', props)}>
        {i18n.t('page.telephony.recording.unavailable')}
      </Text>
    );
  }

  const refreshExpiredLink = (event: SyntheticEvent<HTMLAudioElement>) => {
    // `expiresAt` is on the server clock. A link is valid for at least a minute after it arrives,
    // so a viewer's clock running ahead cannot make each new link look expired and refetch forever.
    const isNewLink =
      isNotNil(fulfilledTimeStamp) && Date.now() - fulfilledTimeStamp < LINK_MIN_VALIDITY_MS;

    if (isNewLink || !isAfter(new Date(), parseDate(recording.expiresAt))) {
      return;
    }

    const position = event.currentTarget.currentTime;
    refetch()
      .unwrap()
      .then(() => setResumeAt(position))
      .catch(handleApiError);
  };

  // A media fragment starts a new link where the old one stopped.
  const src = isNil(resumeAt) ? recording.uri : `${recording.uri}#t=${resumeAt}`;

  return (
    <Box width="100%">
      {/* The signed link is a bearer URL: keep it out of session replays. */}
      <Audio
        controls
        controlsList="nodownload"
        autoPlay
        src={src}
        data-dd-privacy="hidden"
        onPlay={refreshExpiredLink}
        onError={refreshExpiredLink}
        data-testid={suffixTestId('audio', props)}
      />
    </Box>
  );
}

const Audio = styled.audio`
  display: block;
  width: 100%;
`;
